import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SignOutAllResult, Visit } from '@cloudsign/shared';
import type { TodayBoard } from '@cloudsign/shared';
import { ApiClientError, api } from '../../lib/api';
import { errorText, t } from '../../lib/i18n';
import { Alert, Button, SelectField, TextField } from '../../components/ui';
import { localToIso } from './format';
import { Overlay } from './parts';

const message = (e: unknown): string => (e instanceof ApiClientError ? errorText(e.body) : t('error.internal'));

/** Sign one person out, now or at an earlier time. */
export function SignOutDialog({
  visitId,
  name,
  timeZone,
  onClose,
  onDone,
}: {
  visitId: string;
  name: string;
  timeZone: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [when, setWhen] = useState('');
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.post(`/visits/${visitId}/sign-out`, { body: { signedOutAt: localToIso(when, timeZone), answers: [] }, schema: Visit }),
    onSuccess: onDone,
    onError: (e) => setError(message(e)),
  });
  return (
    <Overlay
      title={t('today.signOut.title', { name })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={m.isPending} onClick={() => m.mutate()}>
            {t('today.signOut.submit')}
          </Button>
        </>
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      <TextField label={t('today.signOut.time')} hint={t('today.signOut.timeHint')} type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
    </Overlay>
  );
}

/** Sign out everyone who is in at the site, or just one group. */
export function SignOutAllDialog({
  siteId,
  groups,
  onClose,
  onDone,
}: {
  siteId: string;
  groups: TodayBoard['groups'];
  onClose: () => void;
  onDone: (count: number) => void;
}) {
  const [groupId, setGroupId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.post('/visits/sign-out-all', { body: { siteId, ...(groupId ? { groupId } : {}) }, schema: SignOutAllResult }),
    onSuccess: (r) => onDone(r.signedOut),
    onError: (e) => setError(message(e)),
  });
  return (
    <Overlay
      title={t('today.signOutAll.title')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="danger" disabled={m.isPending} onClick={() => m.mutate()}>
            {t('today.signOutAll.submit')}
          </Button>
        </>
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      <p className="text-sm text-slate-700">{t('today.signOutAll.text')}</p>
      <SelectField label={t('today.signOutAll.group')} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
        <option value="">{t('today.signOutAll.everyone')}</option>
        {groups.map((g) => (
          <option key={g.groupId} value={g.groupId}>
            {g.name}
          </option>
        ))}
      </SelectField>
    </Overlay>
  );
}
